import { EventNewID } from './eventlog/eventNewConfig';
import { trackEvent } from './eventlog/logAdapter';

/**
 * 用户在线时长追踪类
 * 功能：
 * 1. 记录用户在页面可见状态下的在线时长
 * 2. 过滤无效数据（秒开秒关、快速刷新等）
 * 3. 检测用户活跃度（交互行为）
 * 4. 支持心跳上报和离开时最终上报
 */
class OnlineTimeTracker {
  private startTime: number = 0;
  private totalDuration: number = 0;
  private isActive: boolean = false;
  private isPageVisible: boolean = true;

  // 用户活跃度追踪
  private userActivity = {
    hasInteraction: false, // 是否有交互
    count: 0, // 交互次数
    lastTime: 0, // 最后一次交互时间
  };

  // 配置阈值
  private readonly THRESHOLDS = {
    MIN_VALID: 3, // 最小有效时长（秒）
    MIN_WITH_ACTION: 2, // 有交互时的最小时长（秒）
    REFRESH_IGNORE: 5, // 刷新后多少秒内不计入（秒）
    ACTIVITY_WINDOW: 300, // 无操作多久算不活跃（秒）
    MIN_INTERACTIONS: 3, // 至少多少次交互才算活跃会话
    MAX_VALID: 14400, // 最大有效时长（秒，4小时）
    MAX_INACTIVE: 3600, // 最长无操作时长（秒，1小时）
  };

  // 心跳上报定时器
  private heartbeatTimer: NodeJS.Timeout | null = null;

  // 是否已初始化
  private initialized: boolean = false;

  // 是否正在上报
  private isReporting: boolean = false;

  // 单例模式
  private static instance: OnlineTimeTracker | null = null;

  private constructor() {
    // 私有构造函数
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): OnlineTimeTracker {
    if (!OnlineTimeTracker.instance) {
      OnlineTimeTracker.instance = new OnlineTimeTracker();
    }
    return OnlineTimeTracker.instance;
  }

  /**
   * 初始化追踪器
   */
  public init(): void {
    if (this.initialized) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    this.startTime = Date.now();
    this.isActive = true;
    this.isPageVisible = !document.hidden;
    this.userActivity.lastTime = Date.now();
    this.initialized = true;

    // 监听用户活动
    this.trackUserActivity();

    // 监听页面可见性变化
    this.trackPageVisibility();

    // 监听页面卸载
    this.trackPageUnload();

    // 启动心跳上报（可选）
    // this.startHeartbeat();
  }

  /**
   * 追踪用户活动
   */
  private trackUserActivity(): void {
    const events = ['click', 'scroll', 'keydown', 'mousemove', 'touchstart'];
    const handler = this.debounce(() => {
      this.userActivity.hasInteraction = true;
      this.userActivity.count++;
      this.userActivity.lastTime = Date.now();
    }, 1000);

    events.forEach((event) => {
      document.addEventListener(event, handler, { passive: true });
    });
  }

  /**
   * 追踪页面可见性
   */
  private trackPageVisibility(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // 页面隐藏，暂停计时
        this.pauseTracking();
        this.isPageVisible = false;
      } else {
        // 页面显示，恢复计时
        this.isPageVisible = true;
        this.resumeTracking();
      }
    });
  }

  /**
   * 追踪页面卸载
   */
  private trackPageUnload(): void {
    // beforeunload 事件
    window.addEventListener('beforeunload', () => {
      this.report();
    });

    // pagehide 事件（更可靠，特别是移动端）
    window.addEventListener('pagehide', () => {
      this.report();
    });

    // 针对移动端的 visibilitychange
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.report();
      }
    });
  }

  /**
   * 暂停计时
   */
  private pauseTracking(): void {
    if (!this.isActive) return;

    const duration = (Date.now() - this.startTime) / 1000;

    // 只有时长大于等于最小有效时长才累加
    if (duration >= this.THRESHOLDS.MIN_VALID) {
      this.totalDuration += duration;
    }

    this.isActive = false;
  }

  /**
   * 恢复计时
   */
  private resumeTracking(): void {
    if (this.isActive) return;

    this.startTime = Date.now();
    this.isActive = true;
  }

  /**
   * 判断是否是刷新操作
   */
  private isRefresh(): boolean {
    try {
      if (typeof window === 'undefined' || !window.performance) {
        return false;
      }

      const navEntry = performance.getEntriesByType(
        'navigation',
      )[0] as PerformanceNavigationTiming;
      return navEntry && navEntry.type === 'reload';
    } catch (error) {
      console.error('[OnlineTimeTracker] Error checking refresh:', error);
      return false;
    }
  }

  /**
   * 判断是否应该上报
   */
  private shouldReport(): boolean {
    const duration = this.totalDuration;

    // 过滤1：时长太短
    if (duration < this.THRESHOLDS.MIN_VALID) {
      return false;
    }

    // 过滤2：刷新操作且时长很短
    if (this.isRefresh() && duration < this.THRESHOLDS.REFRESH_IGNORE) {
      return false;
    }

    // 过滤3：长时间但无交互（可能挂机）
    if (duration > 60 && this.userActivity.count < this.THRESHOLDS.MIN_INTERACTIONS) {
      return false;
    }

    // 过滤4：超长时间但很久没活跃（截断到最后活跃时间）
    const inactiveDuration = (Date.now() - this.userActivity.lastTime) / 1000;
    if (inactiveDuration > this.THRESHOLDS.MAX_INACTIVE) {
      this.totalDuration = Math.max(0, this.totalDuration - inactiveDuration);
    }

    return true;
  }

  /**
   * 上报数据
   */
  private report(): void {
    // 正在上报中，阻止后续上报
    if (this.isReporting) {
      return;
    }

    // 如果当前正在活跃，先暂停计时
    if (this.isActive) {
      this.pauseTracking();
    }

    // 判断是否应该上报
    if (!this.shouldReport()) {
      return;
    }

    this.isReporting = true;

    // 封顶最大时长
    const finalDuration = Math.min(
      Math.round(this.totalDuration),
      this.THRESHOLDS.MAX_VALID,
    );

    const data = {
      duration: finalDuration,
      hasInteraction: this.userActivity.hasInteraction,
      interactionCount: this.userActivity.count,
      isRefresh: this.isRefresh(),
    };

    try {
      trackEvent(EventNewID.LOGIN_OUT, data);
      // 上报成功后重置数据，防止重复上报
      this.totalDuration = 0;
      this.userActivity = {
        hasInteraction: false,
        count: 0,
        lastTime: Date.now(),
      };
    } finally {
      this.isReporting = false;
    }
  }

  /**
   * 停止心跳
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 防抖函数
   */
  private debounce(fn: Function, delay: number): (...args: any[]) => void {
    let timer: NodeJS.Timeout;
    return function (this: any, ...args: any[]) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /**
   * 手动触发上报（供外部调用）
   */
  public manualReport(): void {
    this.report();
  }

  /**
   * 获取当前统计信息（调试用）
   */
  public getStats() {
    return {
      totalDuration: this.totalDuration,
      isActive: this.isActive,
      isPageVisible: this.isPageVisible,
      userActivity: { ...this.userActivity },
      initialized: this.initialized,
    };
  }

  /**
   * 销毁实例
   */
  public destroy(): void {
    this.stopHeartbeat();
    this.report();
    this.initialized = false;
  }
}

// 导出单例实例
export default OnlineTimeTracker;
